"use client";

import { useEffect, useRef, useState } from "react";
import { Notice, PillGroup, TextField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  findKaomoji,
  MOODS,
  type Mood,
  randomKaomoji,
} from "@/lib/tool-kit/random-fun/kaomoji";

/** English strings. A translated content file overrides these through `ui`. */
export const KAOMOJI_PICKER_STRINGS = {
  searchLabel: "Search by mood or name",
  searchPlaceholder: "happy, shrug, cat, sleepy…",
  moodLabel: "Mood",
  moodAll: "All",
  moods: {
    happy: "Happy",
    love: "Love",
    celebrate: "Celebrate",
    greeting: "Hello",
    thanks: "Thanks and bow",
    cool: "Cool",
    animals: "Animals",
    shy: "Shy",
    surprised: "Surprised",
    confused: "Confused",
    thinking: "Thinking",
    shrug: "Shrug",
    nervous: "Nervous",
    sleepy: "Sleepy",
    sad: "Sad",
    annoyed: "Annoyed",
  },
  terms: {
    happy: "happy|smile|smiling|joy|glad|grin|cheerful",
    love: "love|heart|hug|crush|affection|kiss",
    celebrate: "celebrate|party|cheer|yay|excited|hooray|congrats",
    greeting: "hello|hi|wave|hey|goodbye|bye|greeting",
    thanks: "thanks|thank|bow|sorry|please|apology|grateful",
    cool: "cool|sunglasses|awesome|confident|thumbs",
    animals: "animal|animals|pet|cute|bear|cat|dog",
    shy: "shy|blush|embarrassed|bashful",
    surprised: "surprised|shock|shocked|wow|gasp|astonished",
    confused: "confused|puzzled|huh|unsure|question",
    thinking: "thinking|think|hmm|ponder|suspicious|doubt",
    shrug: "shrug|whatever|dunno|idk|who knows|meh",
    nervous: "nervous|awkward|sweat|worried|anxious|oops",
    sleepy: "sleepy|sleep|tired|nap|zzz|yawn|bed",
    sad: "sad|cry|crying|tears|unhappy|upset|disappointed",
    annoyed: "annoyed|angry|mad|grumpy|frustrated|table|flip|irritated",
  },
  random: "Copy a random kaomoji",
  resultsHeading: "Kaomoji",
  count: "one: {count} kaomoji|other: {count} kaomoji",
  copyHint: "Click or tap a kaomoji to copy it.",
  copyFace: "Copy {face}",
  copiedMessage: "Copied {face}",
  failedMessage: "Could not copy. Select the kaomoji and copy it by hand.",
  noMatch: "No kaomoji match that search. Try a mood such as happy or sad.",
  lastCopied: "Copied:",
  fontNote:
    "Kaomoji are plain text, so they paste anywhere. A font that lacks one of the characters may show a box or shift the face.",
} as const;

/**
 * How a face is shown on the page. A backslash before an underscore is the inline-markup
 * escape everywhere else on the site, and a zero-width space between the two keeps a
 * shrug's arm from reading as an unresolved escape; the copied text is untouched.
 */
function shown(face: string): string {
  return face.replaceAll("\\_", "\\\u200B_");
}

export default function KaomojiPickerWidget() {
  const strings = useToolUi(KAOMOJI_PICKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mood, setMood] = useState<Mood | "all">("all");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [lastFace, setLastFace] = useState("");
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (clearTimer.current) {
        clearTimeout(clearTimer.current);
      }
    },
    []
  );

  const entries = findKaomoji(mood, query, strings.terms);

  async function copy(face: string) {
    const ok = await writeToClipboard(face);
    setLastFace(ok ? face : "");
    setStatus(
      ok
        ? t(strings.copiedMessage, { face: shown(face) })
        : strings.failedMessage
    );
    if (clearTimer.current) {
      clearTimeout(clearTimer.current);
    }
    clearTimer.current = setTimeout(() => setStatus(""), 4000);
  }

  function pickRandom() {
    const entry = randomKaomoji(entries);
    if (entry) {
      copy(entry.face);
    }
  }

  return (
    <div className="space-y-6">
      <TextField
        id="kp-search"
        label={strings.searchLabel}
        onChange={setQuery}
        placeholder={strings.searchPlaceholder}
        type="search"
        value={query}
      />
      <PillGroup
        label={strings.moodLabel}
        name="kp-mood"
        onChange={setMood}
        options={[
          { value: "all", label: strings.moodAll },
          ...MOODS.map((value) => ({ value, label: strings.moods[value] })),
        ]}
        value={mood}
      />
      <div className="flex flex-wrap items-center gap-3">
        <button className={BUTTON_SECONDARY} onClick={pickRandom} type="button">
          {strings.random}
        </button>
        <output aria-live="polite" className="text-foreground text-sm">
          {status}
        </output>
      </div>
      {lastFace ? (
        <p className="break-words font-mono text-2xl text-foreground">
          <span className="mr-2 text-muted-foreground text-sm">
            {strings.lastCopied}
          </span>
          {shown(lastFace)}
        </p>
      ) : null}

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        <p className={HINT}>
          {tPlural(strings.count, locale, entries.length, {
            count: fmt(entries.length),
          })}
          {" · "}
          {strings.copyHint}
        </p>
        {entries.length === 0 ? (
          <Notice>{strings.noMatch}</Notice>
        ) : (
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {entries.map((entry) => (
              <li key={entry.face}>
                <button
                  aria-label={t(strings.copyFace, { face: shown(entry.face) })}
                  className="flex min-h-12 w-full items-center justify-center break-all rounded-lg border border-border bg-card px-2 py-2 text-center text-base text-foreground transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
                  onClick={() => copy(entry.face)}
                  title={strings.moods[entry.mood]}
                  type="button"
                >
                  {shown(entry.face)}
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className={HINT}>{strings.fontNote}</p>
      </section>
    </div>
  );
}
